<script setup>
import Section from "../internal/Section.vue";
import EditableField from "../fields/EditableField.vue";
import ReadonlyField from "../fields/ReadonlyField.vue";
import Button from "@/shared/ui/button/Button.vue";
import SvgIcon from "@/shared/ui/svg-icon/SvgIcon.vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

defineProps({
  data: { type: Object, required: true },
  editMode: { type: Boolean, default: false },
  isOwn: { type: Boolean, default: false },
});

const emit = defineEmits([
  "update-array-item",
  "add-array-item",
  "remove-array-item",
]);

const TEMPLATE = { name: "", number: "", issue_date: "", issued_by: "" };
const NAME_OPTS = ["Удостоверение личности", "Паспорт", "Вид на жительство"];

function pluralDoc(n) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} ${t("документ")}`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20))
    return `${n} ${t("документа")}`;
  return `${n} ${t("документов")}`;
}

function setItem(idx, key, value) {
  emit("update-array-item", "documents", idx, key, value);
}
function addItem() {
  emit("add-array-item", "documents", TEMPLATE);
}
function removeItem(idx) {
  emit("remove-array-item", "documents", idx);
}
</script>

<template>
  <Section
    icon="sli-identification"
    :title="$t('Документы, удостоверяющие личность')"
    :sub="pluralDoc((data.documents || []).length)"
  >
    <template v-if="editMode" #actions>
      <Button variant="outline" size="sm" @click="addItem">
        <SvgIcon
          name="huge-plus"
          style="width: 13px; height: 13px; fill: none"
        />{{ $t("Добавить") }}
      </Button>
    </template>

    <div class="flex flex-col gap-2.5">
      <div
        v-for="(doc, i) in data.documents || []"
        :key="i"
        class="border border-border p-3.5 hover:border-input transition-colors"
        style="border-radius: var(--radius)"
      >
        <div class="flex items-center gap-2.5 mb-3">
          <div
            class="w-7 h-7 bg-card border border-border flex items-center justify-center text-[12px] font-semibold text-foreground/70"
            style="border-radius: var(--radius)"
          >
            {{ i + 1 }}
          </div>
          <div class="text-[13px] font-semibold text-foreground">
            {{ doc.name ? $t(doc.name) : $t("Документ") }}
          </div>
          <div class="ml-auto">
            <Button
              v-if="editMode"
              variant="ghost"
              size="icon-sm"
              class="w-7 h-7"
              @click="removeItem(i)"
            >
              <SvgIcon name="huge-close" class="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div class="grid grid-cols-2 gap-x-5 gap-y-3.5 max-sm:grid-cols-1">
          <template v-if="editMode">
            <EditableField
              :label="$t('Наименование')"
              required
              :model-value="doc.name"
              :options="NAME_OPTS.map((o) => ({ value: o, label: $t(o) }))"
              @update:model-value="setItem(i, 'name', $event)"
            />
            <EditableField
              :label="$t('Номер')"
              required
              :model-value="doc.number"
              @update:model-value="setItem(i, 'number', $event)"
            />
            <EditableField
              :label="$t('Дата выдачи')"
              type="date"
              required
              :model-value="doc.issue_date"
              @update:model-value="setItem(i, 'issue_date', $event)"
            />
            <EditableField
              :label="$t('Кем выдан')"
              :model-value="doc.issued_by"
              @update:model-value="setItem(i, 'issued_by', $event)"
            />
          </template>
          <template v-else>
            <ReadonlyField
              :label="$t('Наименование')"
              :value="doc.name ? $t(doc.name) : doc.name"
            />
            <ReadonlyField :label="$t('Номер')" :value="doc.number" />
            <ReadonlyField
              :label="$t('Дата выдачи')"
              :value="doc.issue_date"
              type="date"
            />
            <ReadonlyField :label="$t('Кем выдан')" :value="doc.issued_by" />
          </template>
        </div>
      </div>

      <div
        v-if="!(data.documents || []).length"
        class="text-sm text-muted-foreground py-2"
      >
        {{ $t("Нет документов") }}
      </div>

      <button
        v-if="editMode"
        type="button"
        class="flex items-center justify-center gap-1.5 w-full py-2.5 border border-dashed border-input text-sm text-muted-foreground hover:text-foreground hover:bg-muted hover:border-foreground/40 transition-colors"
        style="border-radius: var(--radius)"
        @click="addItem"
      >
        <SvgIcon
          name="huge-plus"
          style="width: 14px; height: 14px; fill: none"
        />{{ $t("Добавить документ") }}
      </button>
    </div>
  </Section>
</template>
